import { useMemo } from 'react';
import dayjs from 'dayjs';
import { CallIntelConstant, CallIntelligenceV2, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';

export interface UseCallIntelTrialStatsProps {
  locationIds: string[];
  minimumTrialStartDate: Date | null;
  maximumTrialEndDate?: Date | null;
  enabled?: boolean;
}

export interface CallIntelTrialStats {
  unhappyCalls: number;
  revenueAtRisk: number;
  schedulingRate: number;
  isLoading: boolean;
}

export const useCallIntelTrialStats = ({
  locationIds,
  minimumTrialStartDate,
  maximumTrialEndDate,
  enabled = true,
}: UseCallIntelTrialStatsProps): CallIntelTrialStats => {
  const { overviewRequestPayload, schedulingStatsRequestPayload, isQueryEnabled } = useMemo(() => {
    const hasLocations = locationIds.length > 0;
    const hasStartDate = !!minimumTrialStartDate;
    const _isQueryEnabled = enabled && hasLocations && hasStartDate;

    if (!_isQueryEnabled) {
      return {
        overviewRequestPayload: null,
        schedulingStatsRequestPayload: null,
        isQueryEnabled: false,
      };
    }

    const filters = {
      locations: locationIds,
      startDate: dayjs(minimumTrialStartDate).format('YYYY-MM-DD'),
      endDate: maximumTrialEndDate ? dayjs(maximumTrialEndDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    };

    const _overviewRequestPayload = {
      ...CallIntelUtils.generateChartsPayload({ filters }),
      includesAnalysis: [CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT],
      includesHydration: [],
    };

    const _schedulingStatsRequestPayload = {
      filter: CallIntelUtils.generateChartsPayload({ filters })?.filter,
      includeComparison: false,
      isDemo: false,
    };

    return {
      overviewRequestPayload: _overviewRequestPayload,
      schedulingStatsRequestPayload: _schedulingStatsRequestPayload,
      isQueryEnabled: _isQueryEnabled,
    };
  }, [locationIds, minimumTrialStartDate, maximumTrialEndDate, enabled]);

  const { data: overviewData, isLoading: isOverviewLoading } = CallIntelligenceV2.Queries.useGetCallsOverviewQuery({
    request: overviewRequestPayload || {},
    options: {
      enabled: isQueryEnabled && !!overviewRequestPayload,
      retry: false,
      cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
    },
  });

  const { data: schedulingAggregateStats, isLoading: isSchedulingLoading } =
    CallIntelligenceV2.Queries.useGetSchedulingAggregateStatsQuery({
      request: schedulingStatsRequestPayload || {},
      options: {
        enabled: isQueryEnabled && !!schedulingStatsRequestPayload,
        retry: false,
        cacheTime: CallIntelConstant.DEFAULT_CACHE_TIME,
        staleTime: CallIntelConstant.DEFAULT_CACHE_TIME,
      },
    });

  return useMemo(() => {
    if (!isQueryEnabled) {
      return {
        unhappyCalls: 0,
        revenueAtRisk: 0,
        schedulingRate: 0,
        isLoading: false,
      };
    }

    const unhappyCalls = overviewData?.sentiments?.['SENTIMENT_NEGATIVE'] || 0;
    const revenueAtRisk = schedulingAggregateStats?.current?.stillUnscheduledCount || 0;

    const scheduledOnCallCount = schedulingAggregateStats?.current?.scheduledOnCallCount || 0;
    const scheduledOpportunitiesCount = schedulingAggregateStats?.current?.scheduledOpportunitiesCount || 0;

    const schedulingRate =
      scheduledOpportunitiesCount > 0 ? Math.round((scheduledOnCallCount / scheduledOpportunitiesCount) * 100) : 0;

    return {
      unhappyCalls,
      revenueAtRisk,
      schedulingRate,
      isLoading: isOverviewLoading || isSchedulingLoading,
    };
  }, [isQueryEnabled, overviewData, schedulingAggregateStats, isOverviewLoading, isSchedulingLoading]);
};
